import { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  SimpleGrid,
  Image,
  Box,
  Text,
  Spinner,
  Center,
  useToast,
  IconButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import styled from "styled-components";
import { getClientAssets, ClientAsset, deleteClientAsset } from "../../api/admin/adminCampaign";
import { useAdminAuth } from "../../contexts/AdminAuthContext";

const ImageCard = styled(Box)<{ $selected: boolean }>`
  cursor: pointer;
  border: 3px solid ${(props) => (props.$selected ? "#339CFF" : "#e2e8f0")};
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
  position: relative;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    border-color: ${(props) => (props.$selected ? "#339CFF" : "#cbd5e0")};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:hover .delete-button {
    opacity: 1;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 150px;
  position: relative;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DeleteButton = styled(IconButton)`
  position: absolute !important;
  top: 8px !important;
  right: 8px !important;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
  background: rgba(229, 62, 62, 0.95) !important;
  color: white !important;
  border-radius: 6px !important;
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  padding: 0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;

  &:hover {
    background: rgba(229, 62, 62, 1) !important;
    transform: scale(1.1);
  }

  svg {
    width: 16px !important;
    height: 16px !important;
  }
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  onSelectImage: (url: string) => void;
}

export const ImageAssetPicker = ({ isOpen, onClose, clientId, onSelectImage }: Props) => {
  const { adminToken } = useAdminAuth();
  const toast = useToast();
  const [assets, setAssets] = useState<ClientAsset[]>([]);
  const [selectedUrl, setSelectedUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<ClientAsset | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef(null);

  useEffect(() => {
    if (isOpen && clientId && adminToken) {
      loadAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clientId, adminToken]);

  const loadAssets = async () => {
    if (!adminToken || !clientId) return;
    setIsLoading(true);
    try {
      const data = await getClientAssets(adminToken, clientId);
      // Filter out assets without valid URLs (must start with http:// or https://)
      const validAssets = data.filter(asset => {
        if (!asset.url || asset.url.trim() === "") return false;
        // Check if URL starts with http:// or https://
        return asset.url.startsWith("http://") || asset.url.startsWith("https://");
      });
      setAssets(validAssets);
    } catch (error) {
      console.error("Failed to load assets:", error);
      toast({
        title: "Failed to load images",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = () => {
    if (selectedUrl) {
      onSelectImage(selectedUrl);
      onClose();
      setSelectedUrl("");
    }
  };

  const handleDeleteClick = (asset: ClientAsset, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent selecting the image
    setAssetToDelete(asset);
    onDeleteOpen();
  };

  const handleConfirmDelete = async () => {
    if (!adminToken || !assetToDelete) return;
    setIsDeleting(true);
    try {
      await deleteClientAsset(adminToken, clientId, assetToDelete.url);
      toast({
        title: "Image deleted successfully",
        status: "success",
        duration: 2000,
      });
      // Remove from local state
      setAssets(assets.filter((a) => a.url !== assetToDelete.url));
      onDeleteClose();
      setAssetToDelete(null);
    } catch (error) {
      console.error("Failed to delete asset:", error);
      toast({
        title: "Failed to delete image",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent maxH="90vh">
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" pb={3}>
          Select Image from Library
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} pt={6} overflowY="auto">
          {isLoading ? (
            <Center py={10}>
              <Spinner size="xl" color="#339CFF" />
            </Center>
          ) : assets.length === 0 ? (
            <Center py={10}>
              <Text color="gray.500">No images uploaded yet. Upload your first image above!</Text>
            </Center>
          ) : (
            <>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4} mb={4}>
                {assets.map((asset) => (
                  <ImageCard
                    key={asset.url}
                    $selected={selectedUrl === asset.url}
                    onClick={() => setSelectedUrl(asset.url)}
                  >
                    <ImageContainer>
                      <DeleteButton
                        className="delete-button"
                        aria-label="Delete image"
                        icon={<DeleteIcon />}
                        size="sm"
                        onClick={(e) => handleDeleteClick(asset, e)}
                      />
                      <StyledImage
                        src={asset.url}
                        alt={asset.name}
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150'%3E%3Crect fill='%23f0f0f0' width='200' height='150'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' text-anchor='middle' dy='.3em' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E"
                        loading="lazy"
                      />
                    </ImageContainer>
                    <Box p={2} bg="gray.50">
                      <Text fontSize="xs" noOfLines={1} title={asset.name}>
                        {asset.name || "Untitled"}
                      </Text>
                    </Box>
                  </ImageCard>
                ))}
              </SimpleGrid>
              <Button
                onClick={handleSelect}
                colorScheme="blue"
                isDisabled={!selectedUrl}
                width="full"
              >
                Select Image
              </Button>
            </>
          )}
        </ModalBody>
      </ModalContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Image
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={2}>
                Are you sure you want to delete <strong>{assetToDelete?.name}</strong>?
              </Text>
              <Text color="red.600" fontWeight="600">
                This action cannot be undone.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                ml={3}
                isLoading={isDeleting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};
